"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/lib/auth-context";
import { EventCard } from "@/components/feed/event-card";
import { DemoBadge } from "@/components/feed/demo-badge";
import { type GalleryPhoto } from "@/components/feed/photo-grid";
import { PhotoLightbox, type LightboxPhoto } from "@/components/feed/photo-lightbox";
import { GardenViewer } from "@/components/garden/garden-viewer";
import { Button } from "@/components/ui/button";
import type { GardenZone, VerticalSupport } from "@/types/garden";
import type { PlacementData } from "@/lib/garden/canvas-renderer";
import { EVENTS, track } from "@/lib/analytics";

type PublicGarden = {
  id: string;
  user_id: string;
  name: string;
  season_year: number;
  orientation: string;
  width_cm: number;
  height_cm: number;
  is_public: boolean;
  is_demo: boolean;
  supports: VerticalSupport[] | null;
  created_at: string;
  updated_at: string;
};

type Owner = {
  first_name: string | null;
  zone_fr: string | null;
};

interface FeedEvent {
  id: string;
  garden_id: string;
  type: string;
  data: Record<string, unknown>;
  created_at: string;
}

type ZonePayload = {
  id: string;
  garden_id: string;
  name: string;
  color_pal: number;
  x: number;
  y: number;
  width_cm: number;
  height_cm: number;
  points: unknown;
};

type PlacementPayload = {
  id: string;
  zone_id: string;
  plant_id: string;
  x: number;
  y: number;
  variety: string | null;
};

export type PublicGardenPayload = {
  garden: PublicGarden;
  owner: Owner;
  zone_count: number;
  placement_count: number;
  zones: ZonePayload[];
  placements: PlacementPayload[];
  photos: GalleryPhoto[];
  events: FeedEvent[];
};

/** Editorial copy for a seeded garden, read from the repository, not the DB. */
export type DemoCopy = { region: string; intro: string };

export function PublicGardenView({
  payload,
  demo = null,
}: {
  payload: PublicGardenPayload;
  demo?: DemoCopy | null;
}) {
  const id = payload.garden.id;
  const router = useRouter();
  const { user } = useAuth();
  const [forking, setForking] = useState(false);
  const [lightbox, setLightbox] = useState<{ photos: LightboxPhoto[]; index: number } | null>(null);

  async function handleFork() {
    if (!user) {
      router.push("/auth/login");
      return;
    }
    setForking(true);

    const res = await fetch(`/api/gardens/${id}/fork`, { method: "POST" });
    const data = (await res.json().catch(() => ({}))) as { id?: string };

    if (!res.ok || !data.id) {
      setForking(false);
      return;
    }
    track(EVENTS.planForked);
    router.push(`/dashboard/garden/${data.id}`);
  }

  const { garden, owner, zone_count, placement_count, zones, placements, photos, events } = payload;
  const areaM2 = ((garden.width_cm * garden.height_cm) / 10000)
    .toFixed(1)
    .replace(".", ",");

  const zonesForViewer: GardenZone[] = zones.map((z) => ({
    id: z.id,
    garden_id: z.garden_id,
    name: z.name,
    color_pal: z.color_pal,
    x: z.x,
    y: z.y,
    width_cm: z.width_cm,
    height_cm: z.height_cm,
    points: (z.points ?? undefined) as
      | [
          { x: number; y: number },
          { x: number; y: number },
          { x: number; y: number },
          { x: number; y: number },
        ]
      | undefined,
  }));

  const placementsForViewer: PlacementData[] = placements.map((p) => ({
    id: p.id,
    zone_id: p.zone_id,
    plant_id: p.plant_id,
    x: p.x,
    y: p.y,
    variety: p.variety ?? undefined,
  }));

  const supportsForViewer: VerticalSupport[] = (garden.supports ?? []) as VerticalSupport[];

  return (
    <>
      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="card mb-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-headline-md text-garden-800">{garden.name}</h1>
                {garden.is_demo && <DemoBadge />}
              </div>
              <p className="text-body-md text-on-surface-variant mt-1">
                {owner.first_name ?? "Un jardinier"}
                {demo ? ` · ${demo.region}` : owner.zone_fr ? ` · Dept. ${owner.zone_fr}` : ""}
                {` · ${areaM2} m²`}
                {` · ${garden.season_year}`}
              </p>
              <p className="text-label-md text-on-surface-variant mt-2">
                {zone_count} planche{zone_count !== 1 ? "s" : ""}
                {" · "}
                {placement_count} plant{placement_count !== 1 ? "s" : ""}
                {photos.length > 0 &&
                  ` · ${photos.length} photo${photos.length !== 1 ? "s" : ""}`}
              </p>
            </div>
            <Button
              onClick={handleFork}
              loading={forking}
              variant="secondary"
              className="!w-auto shrink-0"
            >
              Dupliquer ce plan
            </Button>
          </div>
        </div>

        {demo && (
          <div className="card mb-4 border-l-4 border-garden-300">
            <p className="text-body-md text-on-surface">{demo.intro}</p>
            <p className="text-label-md text-on-surface-variant mt-3">
              Ce potager est un exemple créé par Esprit Potager, pas la saison
              d&apos;un membre. Dupliquez-le pour le modifier chez vous.
            </p>
          </div>
        )}

        {/* Plan — always at the top */}
        <div className="card p-2 mb-6">
          <GardenViewer
            gardenWidthCm={garden.width_cm}
            gardenHeightCm={garden.height_cm}
            zones={zonesForViewer}
            placements={placementsForViewer}
            supports={supportsForViewer}
            className="w-full aspect-[4/3] sm:aspect-video relative rounded-lg overflow-hidden"
          />
        </div>

        {/* Activity feed below the plan */}
        <h2 className="text-title-lg text-on-surface mb-4">Activité</h2>

        {events.length === 0 ? (
          <div className="text-center py-12 card">
            <p className="text-body-md text-on-surface-variant">
              Pas encore d{"'"}activité sur ce potager.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {events.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                gardenName={garden.name}
                gardenId={garden.id}
                firstName={owner.first_name}
                isDemo={garden.is_demo}
                onPhotoClick={(photos, index) => setLightbox({ photos, index })}
              />
            ))}
          </div>
        )}
      </main>

      <PhotoLightbox
        photos={lightbox?.photos ?? []}
        index={lightbox?.index ?? null}
        onClose={() => setLightbox(null)}
        onIndexChange={(index) =>
          setLightbox((prev) => (prev ? { ...prev, index } : prev))
        }
      />
    </>
  );
}
